import { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
} from "@mui/material";
import MobileDrawer from "../menus/MobileDrawer";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { LanguageContext } from "../../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { getApp } from "firebase/app";
import {
  getStripePayments,
  getCurrentUserSubscriptions,
} from "@invertase/firestore-stripe-payments";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../firebase";

const appStripe = getApp();
const paymentsStripe = getStripePayments(appStripe, {
  productsCollection: "products",
  customersCollection: "customers",
});

export default function MobileHeader({
  title,
  showProfileItem = true,
  showGoPremium = true,
  rightContent = null,
}) {
  const { translate } = useContext(LanguageContext);
  const { currentUser, logOut } = useAuth();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [anchorNotif, setAnchorNotif] = useState(null);
  const [anchorProfile, setAnchorProfile] = useState(null);
  const unreadCount = notifications.filter((n) => !n.read).length;

  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    const notifCol = collection(
      db,
      "profilsUsers",
      currentUser.uid,
      "notifications"
    );
    const unsub = onSnapshot(notifCol, (snap) => {
      setNotifications(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    getCurrentUserSubscriptions(paymentsStripe, { status: "active" })
      .then((subs) => setIsPremium(subs.length > 0))
      .catch(console.error);
  }, [currentUser]);

  const handleBellClick = (e) => setAnchorNotif(e.currentTarget);
  const handleBellClose = () => setAnchorNotif(null);
  const handleProfileClick = (e) => setAnchorProfile(e.currentTarget);
  const handleProfileClose = () => setAnchorProfile(null);

  // Trier du plus récent au plus ancien (fallback si timestamp absent)
  const sortedNotifications = notifications
    .slice()
    .sort(
      (a, b) => (b?.timestamp?.seconds ?? 0) - (a?.timestamp?.seconds ?? 0)
    );

  const clearReadNotifications = async () => {
    if (!currentUser) return;
    const toDelete = sortedNotifications.filter((n) => n.read);
    if (toDelete.length === 0) return;
    try {
      await Promise.all(
        toDelete.map((n) =>
          deleteDoc(
            doc(db, "profilsUsers", currentUser.uid, "notifications", n.id)
          )
        )
      );
    } catch (e) {
      console.error("Failed to clear read notifications:", e);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 1,
          bgcolor: "background.paper",
          color: "text.primary",
          width: "100%",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ width: "20%" }}>
          <MobileDrawer />
        </Box>

        <Box sx={{ width: rightContent ? "50%" : "60%" }}>
          <Typography variant="h6" sx={{ textAlign: "center" }}>
            {title}
          </Typography>
        </Box>

        <Box sx={{ width: rightContent ? "30%" : "20%", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
          {rightContent && <Box sx={{ mr: 1 }}>{rightContent}</Box>}
          <IconButton onClick={handleBellClick} color="inherit">
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <IconButton onClick={handleProfileClick} color="inherit">
            <ArrowDropDownIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Menu Notifications */}
      <Menu
        anchorEl={anchorNotif}
        open={Boolean(anchorNotif)}
        onClose={handleBellClose}
        PaperProps={{
          sx: {
            maxHeight: "60vh",
            width: 320,
            bgcolor: "background.paper",
            color: "text.primary",
          },
        }}
      >
        {sortedNotifications.length === 0 ? (
          <MenuItem disabled>
            <ListItemText primary={translate("NoNotifications")} />
          </MenuItem>
        ) : (
          <>
            {sortedNotifications.map((n, idx) => (
              <MenuItem
                key={n.id}
                selected={!n.read} // surbrillance si non lu
                onClick={async () => {
                  if (!currentUser) return;
                  await updateDoc(
                    doc(
                      db,
                      "profilsUsers",
                      currentUser.uid,
                      "notifications",
                      n.id
                    ),
                    { read: true }
                  );
                  handleBellClose();
                  if (n.projectId && n.ticketId) {
                    navigate(
                      `/app/projects/${encodeURIComponent(
                        n.projectId
                      )}/tickets/${encodeURIComponent(n.ticketId)}`
                    );
                  } else if (n.link) {
                    navigate(
                      n.link.startsWith("/app") ? n.link : `/app${n.link}`
                    );
                  }
                }}
                sx={{
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  alignItems: "flex-start",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  ...(idx === sortedNotifications.length - 1 && {
                    borderBottom: "none",
                  }),
                }}
              >
                <ListItemText
                  primary={n.message}
                  secondary={
                    n.timestamp
                      ? new Date(n.timestamp.seconds * 1000).toLocaleString()
                      : ""
                  }
                  primaryTypographyProps={{
                    noWrap: false,
                    sx: { whiteSpace: "normal", overflowWrap: "break-word" },
                  }}
                  secondaryTypographyProps={{
                    noWrap: false,
                    sx: { whiteSpace: "normal" },
                  }}
                />
              </MenuItem>
            ))}

            {sortedNotifications.some((n) => n.read) && (
              <>
                <Divider />
                <MenuItem onClick={clearReadNotifications}>
                  <ListItemText primary={translate("ClearReadNotifications")} />
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      {/* Menu Profil */}
      <Menu
        anchorEl={anchorProfile}
        open={Boolean(anchorProfile)}
        onClose={handleProfileClose}
        PaperProps={{
          sx: { bgcolor: "background.paper", color: "text.primary" },
        }}
      >
        {showProfileItem && (
          <MenuItem
            onClick={() => {
              handleProfileClose();
              navigate("/app/profile-user");
            }}
          >
            <ListItemText primary={translate("Profile")} />
          </MenuItem>
        )}

        {showGoPremium && !isPremium && (
          <MenuItem
            onClick={() => {
              handleProfileClose();
              navigate("/app/premium");
            }}
          >
            <ListItemText primary={translate("GoPremium")} />
          </MenuItem>
        )}

        <MenuItem
          onClick={async () => {
            handleProfileClose();
            await logOut();
            navigate("/login");
          }}
        >
          <ListItemText primary={translate("Logout")} />
        </MenuItem>
      </Menu>
    </>
  );
}
